import { cn } from "./ui";
import { t } from "@/lib/i18n";
import WorkflowCanvas from "./workflow-canvas";
import { workflowStatuses, workflowTemplates, type WorkflowRecord, type WorkflowStatus } from "@/mocks/workflows";

/* Редактор созданного рабочего процесса: слева сведения и список шагов, справа схема. */

const statusMeta = (id: WorkflowStatus) => workflowStatuses.find((s) => s.id === id);
const templateName = (id: string | null) => (id ? workflowTemplates.find((t) => t.id === id)?.name ?? t("шаблон") : null);

const fmt = (iso: string) => {
  const [y, m, d] = iso.split("-");
  return d && m && y ? `${d}.${m}.${y}` : iso;
};

type Props = {
  workflow: WorkflowRecord;
  onBack: () => void;
  onToggle: () => void;
  onRemove: () => void;
  onAddStep: () => void;
};

export default function WorkflowEditor({ workflow, onBack, onToggle, onRemove, onAddStep }: Props) {
  const meta = statusMeta(workflow.status);
  const from = templateName(workflow.template);
  const trigger = workflow.steps.find((s) => s.kind === "trigger")?.text ?? t("Не задан");

  return (
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2.5">
        <button
          type="button"
          onClick={onBack}
          aria-label={t("Назад к рабочим процессам")}
          title={t("Назад")}
          className="grid size-6 flex-none cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
        >
          <i className="ri-arrow-left-s-line text-[15px] leading-none" aria-hidden />
        </button>
        <div className="min-w-0 flex-1">
          <h4 className="truncate text-[13.5px] font-semibold leading-snug">{workflow.name}</h4>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <span className={cn("size-1.5 rounded-full", meta?.dot)} aria-hidden />
              {meta?.label}
            </span>
            <span className="text-border" aria-hidden>
              ·
            </span>
            <span>{from ? t("шаблон «{name}»", { name: from }) : t("создан с нуля")}</span>
            <span className="text-border" aria-hidden>
              ·
            </span>
            <span>{t("обновлён {date}", { date: fmt(workflow.updated) })}</span>
          </p>
        </div>
        <button
          type="button"
          onClick={onRemove}
          aria-label={t("Удалить процесс")}
          title={t("Удалить процесс")}
          className="grid size-7 flex-none cursor-pointer place-items-center rounded-btn border border-border text-muted-foreground transition hover:border-red-500/50 hover:text-red-500"
        >
          <i className="ri-delete-bin-6-line text-[12px] leading-none" aria-hidden />
        </button>
        <button
          type="button"
          onClick={onToggle}
          className={cn(
            "inline-flex h-7 flex-none cursor-pointer items-center gap-1 whitespace-nowrap rounded-btn px-2.5 text-[11px] font-semibold transition",
            workflow.status === "active" ? "border border-border text-foreground/80 hover:border-primary/50" : "bg-primary text-primary-foreground hover:brightness-110",
          )}
        >
          <i className={cn(workflow.status === "active" ? "ri-pause-line" : "ri-play-line", "text-[12px] leading-none")} aria-hidden />
          {workflow.status === "active" ? t("Приостановить") : t("Включить")}
        </button>
      </div>

      <div className="grid lg:grid-cols-[16.5rem_minmax(0,1fr)]">
        <div className="min-w-0 border-b border-border p-3 md:p-3.5 lg:border-b-0 lg:border-e">
          <dl className="grid grid-cols-1 gap-2">
            {[
              { label: t("Триггер"), value: trigger },
              { label: t("Шаблон"), value: from ?? t("Создан с нуля") },
              { label: t("Автор"), value: workflow.author },
              { label: t("Обновлён"), value: fmt(workflow.updated) },
            ].map((row) => (
              <div key={row.label} className="flex items-start justify-between gap-3">
                <dt className="text-[10.5px] text-muted-foreground">{row.label}</dt>
                <dd className="max-w-[62%] text-end text-[10.5px] font-medium leading-relaxed text-foreground/80">{row.value}</dd>
              </div>
            ))}
          </dl>

          <p className="mt-3.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{t("Шаги процесса")}</p>
          <ol className="mt-1.5 flex flex-col gap-1">
            {workflow.steps.map((s, i) => (
              <li key={s.id} className="flex items-start gap-2 rounded-btn border border-border bg-background/50 px-2 py-1.5">
                <span className="grid size-4 flex-none place-items-center rounded-full bg-primary/[.08] text-[9px] font-bold text-primary tabular-nums">{i + 1}</span>
                <span className="min-w-0">
                  <span className="block truncate text-[10.5px] font-semibold">{s.title}</span>
                  {s.text ? <span className="mt-0.5 block truncate text-[9.5px] text-muted-foreground">{s.text}</span> : null}
                </span>
              </li>
            ))}
          </ol>

          <button
            type="button"
            onClick={onAddStep}
            className="mt-2.5 inline-flex h-7 w-full cursor-pointer items-center justify-center gap-1 whitespace-nowrap rounded-btn border border-dashed border-border text-[10.5px] font-semibold text-muted-foreground transition hover:border-primary/50 hover:text-primary"
          >
            <i className="ri-add-line text-[12px] leading-none" aria-hidden />
            {t("Добавить шаг")}
          </button>
        </div>

        <div className="min-w-0 p-2.5 md:p-3">
          <WorkflowCanvas steps={workflow.steps} className="min-h-[24rem]" />
        </div>
      </div>
    </div>
  );
}